import React, { useState, useContext, useEffect } from 'react';
import urljoin from 'url-join';

import { Modal, Typography } from 'antd';
import { useQuery } from '@apollo/client';
import { Device, MainContext } from '../../contexts/mainContext';
import { FETCH_ALL_SERVICES } from './queries';
import DriverManagementDrawer from './components/DriverManagementDrawer/DriverManagementDrawer';
import PassengersManagementDrawer from '../passengers/passengersManagement';
import { ServicesContext } from '../../contexts/servicesContext';
import CreateServiceDrawer from './components/CreateService/CreateServiceDrawer';
import CancelService from './components/CancelService/CancelService';
import EditServiceDrawer from './components/EditService/EditService';
import {
  SearchServicesQuery,
  useSearchServicesLazyQuery,
} from '../../graphql/datamodel.gen';
import { getClient } from '../../apollo';
import { getMobileApk } from '../../utils/mobile';
import _ from 'lodash';
import { NetworkStatus } from '@apollo/client';
import GlobalMap from './components/GlobalMap/GlobalMap';
import ServiceDetails from './components/ServiceDetails/ServiceDetails';
import ServicesTable from './components/ServicesTable/ServicesTable';
import ServiceStats from './components/ServiceStats/ServiceStats';
import ServiceListToolbar from './components/ServiceListToolbar/ServiceListToolbar';
import moment from 'moment';

const { Paragraph } = Typography;

interface IServicesContainerProps {
  isAdmin: boolean;
  isClient: boolean;
  filterServiceId?: string;
}

export default function ServicesContainer({
  isAdmin,
  isClient,
  filterServiceId,
}: IServicesContainerProps) {
  const mainContext = useContext(MainContext);
  const servicesContext = useContext(ServicesContext);
  const [searchResult, setSearchResult] = useState<
    SearchServicesQuery['searchServices']
  >([]);

  const [
    searchService,
    { loading: searchServiceLoading, data: dataSearchService },
  ] = useSearchServicesLazyQuery({
    client: getClient(),
  });

  useEffect(() => {
    setSearchResult([...(dataSearchService?.searchServices || [])]);
  }, [dataSearchService]);

  const onDateUpdate = (
    departureDateMin: moment.Moment,
    departureDateMax: moment.Moment,
  ) => {
    servicesContext.setCurrentDepartureDates([
      departureDateMin,
      departureDateMax,
    ]);
  };

  let response;

  // eslint-disable-next-line react-hooks/rules-of-hooks
  response = useQuery(FETCH_ALL_SERVICES, {
    variables: {
      departureDateMin: servicesContext.departureDateMin.format('YYYY-MM-DD'),
      departureDateMax: servicesContext.departureDateMax.format('YYYY-MM-DD'),
    },
    pollInterval: 20000,
    fetchPolicy: 'cache-and-network',
    notifyOnNetworkStatusChange: true,
  });

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const {
    error,
    data,
    networkStatus,
    refetch,
    loading: loadingServices,
  } = response;

  const services =
    loadingServices && networkStatus === NetworkStatus.setVariables
      ? []
      : data?.services || [];
  const loading =
    (loadingServices && networkStatus === NetworkStatus.setVariables) ||
    networkStatus === NetworkStatus.refetch;

  const _searchService = async (text: string) => {
    searchService({
      variables: {
        text,
      },
    });
  };

  const _resetSearch = () => {
    setSearchResult([]);
  };

  return (
    <ServicesView
      filterServiceId={filterServiceId}
      sortedInfo={servicesContext.sortedInfo}
      setSortedInfo={servicesContext.setSortedInfo}
      setFilteredInfo={servicesContext.setFilteredInfo}
      filteredInfo={servicesContext.filteredInfo}
      searchLoading={searchServiceLoading}
      search={_searchService}
      refetch={refetch}
      resetSearch={_resetSearch}
      searchResult={searchResult}
      device={mainContext.device}
      defaultDates={[
        servicesContext.departureDateMin,
        servicesContext.departureDateMax,
      ]}
      services={services}
      isAdmin={isAdmin}
      isClient={isClient}
      loading={loading}
      onDateUpdate={onDateUpdate}
    />
  );
}

interface IServiceProps {
  filterServiceId: string;
  defaultDates: moment.Moment[];
  refetch: (...args: any[]) => any;
  services: any[];
  isAdmin: boolean;
  isClient: boolean;
  loading: boolean;
  device: Device;
  onDateUpdate: (dateA: moment.Moment, dateB: moment.Moment) => void;
  search: (text: string) => void;
  searchResult: SearchServicesQuery['searchServices'];
  searchLoading: boolean;
  resetSearch: () => any;
  filteredInfo: any;
  sortedInfo: any;
  setFilteredInfo: (filteredInfo: any) => void;
  setSortedInfo: (filteredInfo: any) => void;
}

// @ts-ignore
class ServicesView extends React.Component<IServiceProps> {
  state = {
    dropdownActionsVisible: false,
    searchValue: undefined,
    searchedColumn: undefined,
    showTooltip: {
      column: undefined,
      row: undefined,
    },
    pickerOpened: false,
    actionsEnabled: false,
    selectedServices: [],
    selectedRowKeys: [],
    filteredInfo: {},
    sortedInfo: {},
    searchText: '',
    serviceBriefingDrawerVisible: false,
    passengersDrawerVisible: false,
    modalVisible: false,
    driverManagementDrawerVisible: false,
    modalTitle: '',
    link: '',
    activeRecord: {},
    createServiceDrawerVisible: false,
    mapVisible: false,
    editServiceDrawerVisible: false,
    cancelServiceModalVisible: false,
    columns: [],
  };

  searchInput: any;

  showDriverManagementDrawer = (record: any) => {
    this.setState({
      activeRecord: record,
      driverManagementDrawerVisible: true,
    });
  };

  hideDriverManagementDrawer = () => {
    this.setState({
      activeRecord: {},
      driverManagementDrawerVisible: false,
    });
  };

  showServiceBriefingDrawer = async (record: any, index: any) => {
    this.setState({
      activeRecord: record,
      serviceBriefingDrawerVisible: true,
    });
  };

  hideServiceBriefingDrawer = () => {
    this.setState({
      activeRecord: {},
      serviceBriefingDrawerVisible: false,
    });
  };

  showPassengerDrawer = (record: any) => {
    this.setState({
      activeRecord: record,
      passengersDrawerVisible: true,
    });
  };

  hidePassengerDrawer = () => {
    this.setState({
      passengersDrawerVisible: false,
      activeRecord: {},
    });
  };

  showModal = async (
    type: 'SERVICE_LINK_APP' | 'PASSENGER_APP_LINK',
    serviceId?: string,
  ) => {
    const link =
      type === 'SERVICE_LINK_APP'
        ? this.computeServiceLinkApp(serviceId)
        : await this.getPassangerAppDownloadLink();

    this.setState({
      modalVisible: true,
      modalTitle:
        type === 'SERVICE_LINK_APP'
          ? 'Link al servicio mediante app'
          : 'Link de descarga de la app pasajero',
      link,
    });
  };

  handleModalOk = (e: any) => {
    this.setState({
      modalVisible: false,
      modalTitle: '',
      link: '',
    });
  };

  handleModalCancel = (e: any) => {
    this.setState({
      modalVisible: false,
      modalTitle: '',
      link: '',
    });
  };

  computeServiceLinkApp = (serviceId: string) => {
    return urljoin(
      process.env.REACT_APP_GRAPHQL_ENDPOINT as string,
      `/launch_service/`,
      serviceId,
    );
  };

  computeMobileAppLink = (serviceId: string) => {
    return `mvipsmobile://mvips.net/?serviceId=${serviceId}`;
  };

  getPassangerAppDownloadLink = () => {
    return getMobileApk('passenger');
  };

  onSelectChange = (selectedRowKeys: any) => {
    this.setState({ selectedRowKeys });
  };

  onSelect = (record: any, selected: any, selectedRows: any) => {
    if (selectedRows.length) {
      this.enableActions(selectedRows);
    } else {
      this.disableActions();
    }
  };

  onSelectAll = (selected: any, selectedRows: any, changeRows: any) => {
    if (selectedRows.length) {
      this.enableActions(selectedRows);
    } else {
      this.disableActions();
    }
  };

  enableActions(selectedServices: any) {
    this.setState({
      actionsEnabled: true,
      selectedServices: selectedServices,
    });
  }

  disableActions() {
    this.setState({
      actionsEnabled: false,
      selectedServices: [],
    });
  }

  openMap = () => {
    this.setState({
      mapVisible: true,
    });
  };

  hideMap = () => {
    this.setState({
      mapVisible: false,
    });
  };

  openCreateServiceDrawer = () => {
    this.setState({
      createServiceDrawerVisible: true,
    });
  };

  hideCreateServiceDrawer = () => {
    this.setState(
      {
        createServiceDrawerVisible: false,
      },
      () => {
        this.props.refetch();
      },
    );
  };

  openEditServiceDrawer = (record) => {
    this.setState({
      activeRecord: record,
      editServiceDrawerVisible: true,
    });
  };

  hideEditServiceDrawer = () => {
    this.setState({
      editServiceDrawerVisible: false,
    });
  };

  openCancelServiceModal = (record) => {
    this.setState({
      activeRecord: record,
      cancelServiceModalVisible: true,
    });
  };

  hideCancelServiceModal = () => {
    this.setState({
      activeRecord: {},
      cancelServiceModalVisible: false,
    });
  };

  onServiceSearch = (text: string) => {
    if (!text || text === '') this.clearSearch();
    if (text !== '') {
      this.props.search(text);
    }
  };

  clearSearch = () => {
    this.props.resetSearch();
    this.setState({
      searchValue: undefined,
    });
  };

  private clearAllFilters = () => {
    this.setState({
      searchText: '',
    });

    this.props.setFilteredInfo({});
    this.props.setSortedInfo({});
  };

  /**
   *
   */
  render() {
    const { device } = this.props;

    // const mailTos = this.state.selectedServices.map((selectedService: any) => {
    //   const passengers = (selectedService as any).passengers || [];
    //   const emailList = passengers
    //     .map((passenger: { email: any }) => passenger.email)
    //     .join(';');
    //
    //   const email = constructEmail(
    //     (selectedService as any).volcanoServiceId,
    //     emailList,
    //   );
    //
    //   return email;
    // });

    // const actionsMenu = (
    //   <Menu>
    //     <Menu.Item
    //       onClick={async () => {
    //         for (const mailTo of mailTos) {
    //           if (mailTo) {
    //             const randomnumber = Math.floor(Math.random() * 100 + 1);
    //             window.open(mailTo, '_self', `PopUp${randomnumber}`);
    //             await new Promise(resolve => setTimeout(resolve, 500));
    //           }
    //         }
    //       }}
    //       key="send_email"
    //     >
    //       Enviar email a los pasajeros
    //     </Menu.Item>
    //   </Menu>
    // );

    return (
      <>
        <CancelService
          hide={this.hideCancelServiceModal}
          id={
            (this.state.activeRecord as any)?.volcanoServiceId ||
            (this.state.activeRecord as any)?.id
          }
          visible={this.state.cancelServiceModalVisible}
        />
        <ServiceStats services={this.props.services} />
        <ServiceListToolbar
          clearAllFilters={this.clearAllFilters}
          clearSearch={this.clearSearch}
          defaultDates={this.props.defaultDates}
          device={this.props.device}
          isAdmin={this.props.isAdmin}
          onDateUpdate={this.props.onDateUpdate}
          onServiceSearch={this.onServiceSearch}
          openCreateServiceDrawer={this.openCreateServiceDrawer}
          openMap={this.openMap}
          searchLoading={this.props.searchLoading}
          setSearchValue={(value) => {
            this.setState({
              searchValue: value,
            });
          }}
          searchValue={this.state.searchValue}
        />

        <ServicesTable
          device={device}
          filteredInfo={this.props.filteredInfo}
          isAdmin={this.props.isAdmin}
          isClient={this.props.isClient}
          loading={this.props.loading}
          services={this.props.services}
          openEditServiceDrawer={this.openEditServiceDrawer}
          searchLoading={this.props.searchLoading}
          searchResult={this.props.searchResult}
          showModal={this.showModal}
          showDriverManagementDrawer={this.showDriverManagementDrawer}
          showPassengerDrawer={this.showPassengerDrawer}
          showServiceBriefingDrawer={this.showServiceBriefingDrawer}
          sortedInfo={this.props.sortedInfo}
          setSortedInfo={this.props.setSortedInfo}
          setFilteredInfo={this.props.setFilteredInfo}
        />
        <Modal
          title={this.state.modalTitle}
          onOk={this.handleModalOk}
          onCancel={this.handleModalCancel}
          visible={this.state.modalVisible}
          width={750}
          centered={true}
          style={{ textAlign: 'center' }}
        >
          <Paragraph
            style={{ fontSize: '25px', marginTop: '1em' }}
            copyable
            code
          >
            {this.state.link}
          </Paragraph>
        </Modal>

        <ServiceDetails
          serviceId={
            (this.state.activeRecord as any).volcanoServiceId ||
            (this.state.activeRecord as any).id
          }
          onClose={this.hideServiceBriefingDrawer}
          visible={this.state.serviceBriefingDrawerVisible}
        />
        {this.state.activeRecord && (
          <DriverManagementDrawer
            width={900}
            volcanoServiceId={(this.state.activeRecord as any).volcanoServiceId}
            serviceId={(this.state.activeRecord as any).id}
            onClose={this.hideDriverManagementDrawer}
            visible={this.state.driverManagementDrawerVisible}
          />
        )}

        <PassengersManagementDrawer
          onClose={this.hidePassengerDrawer}
          visible={this.state.passengersDrawerVisible}
          clientId={
            (this.state.activeRecord as any).client
              ? (this.state.activeRecord as any).client.volcanoClientId ||
                (this.state.activeRecord as any).client.id
              : undefined
          }
          serviceId={
            (this.state.activeRecord as any).volcanoServiceId ||
            (this.state.activeRecord as any).id
          }
        />

        {this.props.isAdmin && (
          <GlobalMap visible={this.state.mapVisible} onClose={this.hideMap} />
        )}

        {this.state.createServiceDrawerVisible && (
          <CreateServiceDrawer
            visible={this.state.createServiceDrawerVisible}
            onClose={this.hideCreateServiceDrawer}
          />
        )}
        {(this.state.activeRecord as any)?.id && (
          <EditServiceDrawer
            onClose={this.hideEditServiceDrawer}
            visible={this.state.editServiceDrawerVisible}
            serviceId={(this.state.activeRecord as any).id}
            volcanoServiceId={(this.state.activeRecord as any).volcanoServiceId}
          />
        )}
      </>
    );
  }
}
